import { useState, useEffect, useCallback } from "react";
import { carregarDispensas, carregarDispensasDoMes, carregarAtletas, salvarDispensa, removerDispensa, carregarModalidadesDoNucleo } from "../firebase";
import { TIPOS_DISPENSA, VERDE, VERDE_CLARO, CINZA_ESC, PERIGO } from "../tema";
import { Card, Btn, Input, Sel, StatusBox } from "../componentes";
import useIsMobile from "../useIsMobile";
import SemNucleo from "../componentes/SemNucleo";

export default function SecaoDatas({ userNucleo, isAdmin }) {
  const isMobile = useIsMobile();
  // Núcleo vem do seletor global do cabeçalho (userNucleo) para quem vê todos
  // os núcleos; o coordenador comum fica no próprio. Não há mais seletor
  // próprio aqui — evita o seletor redundante e mantém tudo sincronizado.
  const nucleoAtivo = userNucleo || "";
  const [form, setForm] = useState({
    dataInicio: "", dataFim: "", tipo: "COLETIVA",
    subtipo: TIPOS_DISPENSA.COLETIVA.subtipos[0],
    modalidade: "Todas", nome: "", motivo: "",
  });
  const [filtroTipo, setFiltroTipo] = useState("TODOS");
  // O mês abre no corrente, e é ele que a consulta pede. Antes vinham as
  // 450 dispensas do núcleo a cada abertura, para mostrar as do mês.
  const [filtroMes, setFiltroMes] = useState(() => new Date().toISOString().slice(0, 7));

  const [dispensas, setDispensas] = useState([]);
  const [atletas, setAtletas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState(null);
  const [sucesso, setSucesso] = useState(null);

  // Modalidades do núcleo ativo — vêm do próprio doc do núcleo (Fase 2,
  // passo 4), não mais derivadas dos atletas já cadastrados. Assim uma
  // modalidade nova aparece aqui mesmo antes do primeiro atleta dela.
  const [modalidadesNucleo, setModalidadesNucleo] = useState([]);

  // Carrega dispensas + atletas do núcleo ativo (recarrega ao trocar de núcleo).
  const recarregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      // Mês escolhido, ou o histórico inteiro quando alguém pede.
      const [ano, mes] = (filtroMes || "").split("-").map(Number);
      const [disps, atls] = await Promise.all([
        filtroMes
          ? carregarDispensasDoMes(nucleoAtivo, mes - 1, ano)
          : carregarDispensas(nucleoAtivo),
        carregarAtletas(nucleoAtivo),
      ]);
      setDispensas(disps.sort((a, b) => a.data.localeCompare(b.data)));
      setAtletas(atls);
    } catch (e) {
      setErro("Erro ao carregar os dados: " + (e.message || ""));
    } finally {
      setCarregando(false);
    }
  }, [nucleoAtivo, filtroMes]);

  useEffect(() => { recarregar(); }, [recarregar]);

  useEffect(() => {
    let ativoEfeito = true;
    (async () => {
      try {
        const lista = await carregarModalidadesDoNucleo(nucleoAtivo);
        if (ativoEfeito) setModalidadesNucleo(lista);
      } catch {
        if (ativoEfeito) setModalidadesNucleo([]);
      }
    })();
    return () => { ativoEfeito = false; };
  }, [nucleoAtivo]);

  // Atletas filtrados pela modalidade escolhida no formulário.
  // Atleta inativo fica fora do seletor: dispensa é registro, e quem foi
  // desligado não recebe registro novo. As dispensas já lançadas para ele
  // continuam valendo — só não dá para criar mais.
  const atletasDoForm = atletas.filter(a => {
    if (a.ativo === false) return false;
    if (form.modalidade === "Todas") return true;
    return a.modalidade === form.modalidade;
  });

  function atualizarForm(campo, valor) {
    setForm(f => {
      const novo = { ...f, [campo]: valor };
      if (campo === "tipo") {
        novo.subtipo = TIPOS_DISPENSA[valor]?.subtipos[0] || "";
        // Coletiva afeta todos: modalidade sempre "Todas" (o campo fica oculto).
        if (valor === "COLETIVA") { novo.modalidade = "Todas"; novo.nome = ""; }
        // Por Modalidade exige uma modalidade ESPECÍFICA ("Todas" não é
        // válido aqui — para todas as modalidades, use Coletiva). Se a
        // modalidade atual é "Todas", cai na primeira do núcleo.
        else if (valor === "MODALIDADE") {
          if (!novo.modalidade || novo.modalidade === "Todas") {
            novo.modalidade = modalidadesNucleo[0] || "";
          }
          novo.nome = "";
        }
      }
      // ao trocar a modalidade, limpa o atleta escolhido (pode não pertencer à nova)
      if (campo === "modalidade") novo.nome = "";
      return novo;
    });
  }

  // Gera a lista de datas (ISO, "YYYY-MM-DD") entre início e fim, inclusive,
  // pulando sábado e domingo automaticamente — não há treino nesses dias,
  // então uma dispensa nessas datas não teria efeito nenhum no relatório
  // (só poluiria a lista). Se "fim" estiver vazio, o período é um dia só.
  function gerarIntervaloDatas(inicio, fim) {
    const datas = [];
    const atual = new Date(inicio + "T12:00:00");
    const final = new Date((fim || inicio) + "T12:00:00");
    while (atual <= final) {
      const diaSemana = atual.getDay(); // 0 = domingo, 6 = sábado
      if (diaSemana !== 0 && diaSemana !== 6) {
        datas.push(atual.toISOString().split("T")[0]);
      }
      atual.setDate(atual.getDate() + 1);
    }
    return datas;
  }

  async function adicionar() {
    if (!form.dataInicio) return;
    if (form.dataFim && form.dataFim < form.dataInicio) {
      setErro("A data final não pode ser anterior à data inicial.");
      return;
    }
    // Limite de 1 semana por vez (segunda a sexta) — para períodos mais
    // longos (ex: um mês de viagem), adicionar em blocos semanais separados.
    if (form.dataFim) {
      const diffDias = Math.round((new Date(form.dataFim + "T12:00:00") - new Date(form.dataInicio + "T12:00:00")) / 86400000);
      if (diffDias > 6) {
        setErro("Selecione no máximo uma semana por vez. Para períodos mais longos, adicione em blocos semanais separados.");
        return;
      }
    }
    if (form.tipo === "INDIVIDUAL" && !form.nome) {
      setErro("Selecione o atleta para a dispensa individual.");
      return;
    }
    // Dispensa por modalidade exige uma modalidade específica. "Para todas as
    // modalidades" deve ser cadastrado como Coletiva (afeta o núcleo inteiro).
    if (form.tipo === "MODALIDADE" && (!form.modalidade || form.modalidade === "Todas")) {
      setErro("Escolha uma modalidade específica. Para afetar todas, use uma dispensa Coletiva.");
      return;
    }
    const datas = gerarIntervaloDatas(form.dataInicio, form.dataFim);
    if (datas.length === 0) {
      setErro("O período selecionado não tem nenhum dia útil (segunda a sexta). Fins de semana são ignorados automaticamente.");
      return;
    }
    setSalvando(true);
    setErro(null);
    setSucesso(null);
    try {
      for (const dataDia of datas) {
        const nova = { ...form, data: dataDia, nucleo: nucleoAtivo, id: `${Date.now()}_${dataDia}` };
        await salvarDispensa(nova);
      }
      setForm(f => ({ ...f, dataInicio: "", dataFim: "", nome: "", motivo: "" }));
      setSucesso(datas.length > 1 ? `${datas.length} datas adicionadas.` : null);
      await recarregar();
    } catch (e) {
      setErro("Erro ao salvar: " + (e.message || ""));
    } finally {
      setSalvando(false);
    }
  }

  async function remover(id) {
    setSalvando(true);
    setErro(null);
    try {
      await removerDispensa(id);
      await recarregar();
    } catch (e) {
      setErro("Erro ao remover: " + (e.message || ""));
    } finally {
      setSalvando(false);
    }
  }

  const exibidas = dispensas.filter(d => {
    const tipoOk = filtroTipo === "TODOS" || d.tipo === filtroTipo;
    const mesOk = !filtroMes || d.data.startsWith(filtroMes);
    return tipoOk && mesOk;
  });

  function nomeDia(dataStr) {
    const d = new Date(dataStr + "T12:00:00");
    return d.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "2-digit", year: "numeric" });
  }

  // Sem núcleo escolhido, a tela diz isso em vez de carregar o de
  // outra pessoa. Depois dos hooks, de propósito: sair antes deles
  // quebraria a ordem que o React exige.
  if (!nucleoAtivo) return <SemNucleo oQueFaria="ver as datas especiais deste núcleo" />;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <Card>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 14 }}>Adicionar data especial</div>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
          <Input label="Data início" value={form.dataInicio} onChange={v => atualizarForm("dataInicio", v)} type="date" />
          <Input label="Data fim (opcional, até 1 semana, pulando fins de semana)" value={form.dataFim} onChange={v => atualizarForm("dataFim", v)} type="date" />
          <Sel label="Tipo" value={form.tipo} onChange={v => atualizarForm("tipo", v)}
            options={Object.entries(TIPOS_DISPENSA).map(([k, v]) => ({ value: k, label: v.label }))} />
        </div>
        {form.tipo === "COLETIVA" ? (
          <div style={{ marginBottom: 12 }}>
            <Sel label="Subtipo" value={form.subtipo} onChange={v => atualizarForm("subtipo", v)}
              options={TIPOS_DISPENSA[form.tipo]?.subtipos || []} />
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12, marginBottom: 12 }}>
            <Sel label="Subtipo" value={form.subtipo} onChange={v => atualizarForm("subtipo", v)}
              options={TIPOS_DISPENSA[form.tipo]?.subtipos || []} />
            <Sel label="Modalidade" value={form.modalidade} onChange={v => atualizarForm("modalidade", v)}
              options={form.tipo === "MODALIDADE"
                ? modalidadesNucleo
                : ["Todas", ...modalidadesNucleo]} />
          </div>
        )}
        {form.tipo === "INDIVIDUAL" && (
          <div style={{ marginBottom: 12 }}>
            {atletasDoForm.length > 0 ? (
              <Sel label="Pessoa (atleta ou equipe técnica)" value={form.nome} onChange={v => atualizarForm("nome", v)}
                options={[
                  { value: "", label: "— selecione a pessoa —" },
                  ...atletasDoForm.map(a => ({ value: a.nome, label: `${a.nome} · ${a.modalidade}${a.grupo === "EQUIPE" ? " · Equipe técnica" : ""}` })),
                ]} />
            ) : (
              <div style={{ padding: "9px 12px", background: "#FEF3C7", borderRadius: 10, fontSize: 13, color: "#92400E" }}>
                {carregando ? "Carregando pessoas…" : `Ninguém ${form.modalidade !== "Todas" ? `de ${form.modalidade} ` : ""}encontrado no núcleo ${nucleoAtivo}.`}
              </div>
            )}
          </div>
        )}
        {form.tipo !== "COLETIVA" && (
          <div style={{ marginBottom: 12, padding: "8px 12px", background: TIPOS_DISPENSA[form.tipo]?.corClaro, borderRadius: 10, fontSize: 13, color: TIPOS_DISPENSA[form.tipo]?.cor, fontWeight: 500 }}>
            {TIPOS_DISPENSA[form.tipo]?.desc}
          </div>
        )}
        <div style={{ marginBottom: 14 }}>
          <Input label="Observação / motivo (opcional)" value={form.motivo} onChange={v => atualizarForm("motivo", v)} placeholder="Ex: Semana de provas" />
        </div>
        {erro && <div style={{ marginBottom: 12 }}><StatusBox tipo="erro" msg={erro} /></div>}
        {sucesso && <div style={{ marginBottom: 12 }}><StatusBox tipo="ok" msg={sucesso} /></div>}
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Btn onClick={adicionar} disabled={!form.dataInicio || salvando}>{salvando ? "Salvando…" : "+ Adicionar"}</Btn>
        </div>
      </Card>

      {/* Filtros */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: CINZA_ESC }}>Filtrar:</span>
        {["TODOS", "COLETIVA", "MODALIDADE", "INDIVIDUAL"].map(t => (
          <button key={t} onClick={() => setFiltroTipo(t)} style={{
            padding: "5px 14px", borderRadius: 99, fontSize: 13, fontWeight: 600, cursor: "pointer",
            border: filtroTipo === t ? `1.5px solid ${VERDE}` : "1px solid #D1D5DB",
            background: filtroTipo === t ? VERDE_CLARO : "transparent",
            color: filtroTipo === t ? VERDE : CINZA_ESC,
          }}>
            {t === "TODOS" ? "Todos" : TIPOS_DISPENSA[t]?.label || t}
          </button>
        ))}
        <input type="month" value={filtroMes} onChange={e => setFiltroMes(e.target.value)}
          style={{ padding: "5px 10px", borderRadius: 10, border: "1px solid #D1D5DB", fontSize: 13 }} />
        {/* O histórico inteiro continua a um clique, e só a um clique: são
            450 dispensas hoje, e elas se acumulam mês a mês. */}
        {filtroMes ? (
          <button onClick={() => setFiltroMes("")}
            style={{ background: "none", border: "none", cursor: "pointer", color: VERDE, fontSize: 13, fontWeight: 600 }}>
            ver todos os meses
          </button>
        ) : (
          <button onClick={() => setFiltroMes(new Date().toISOString().slice(0, 7))}
            style={{ background: "none", border: "none", cursor: "pointer", color: PERIGO, fontSize: 13, fontWeight: 600 }}>
            ✕ voltar ao mês
          </button>
        )}
      </div>

      {/* Lista */}
      {carregando ? (
        <Card style={{ textAlign: "center", color: CINZA_ESC, padding: "32px 24px" }}>
          <div style={{ fontSize: 14 }}>Carregando dispensas do núcleo {nucleoAtivo}…</div>
        </Card>
      ) : exibidas.length === 0 ? (
        <Card style={{ textAlign: "center", color: CINZA_ESC, padding: "32px 24px" }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>📅</div>
          <div style={{ fontSize: 14 }}>
            {filtroMes
              ? "Nenhuma data especial neste mês. Use \"ver todos os meses\" para procurar no histórico."
              : "Nenhuma data especial cadastrada neste núcleo."}
          </div>
        </Card>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {exibidas.map(d => {
            const cfg = TIPOS_DISPENSA[d.tipo];
            return (
              <Card key={d.id} style={{ padding: "14px 18px", display: "flex", alignItems: "center", gap: 14 }}>
                <div style={{ background: cfg.corClaro, color: cfg.cor, fontWeight: 800, fontSize: 11, padding: "4px 10px", borderRadius: 6, whiteSpace: "nowrap", minWidth: 65, textAlign: "center" }}>
                  {cfg.label.toUpperCase()}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{nomeDia(d.data)}</div>
                  <div style={{ fontSize: 12, color: CINZA_ESC, marginTop: 2 }}>
                    {d.subtipo}
                    {d.tipo === "MODALIDADE" && d.modalidade !== "Todas" ? ` · 📌 ${d.modalidade}` : ""}
                    {d.tipo === "INDIVIDUAL" && d.nome ? ` · 👤 ${d.nome}` : ""}
                    {d.motivo ? ` — ${d.motivo}` : ""}
                  </div>
                </div>
                <button onClick={() => remover(d.id)} disabled={salvando} style={{ background: "none", border: "none", cursor: salvando ? "not-allowed" : "pointer", color: "#9CA3AF", fontSize: 20, lineHeight: 1 }}>×</button>
              </Card>
            );
          })}
        </div>
      )}
      {!carregando && exibidas.length > 0 && (
        <div style={{ fontSize: 12, color: CINZA_ESC, textAlign: "right" }}>
          {exibidas.length} data{exibidas.length !== 1 ? "s" : ""}{dispensas.length !== exibidas.length ? ` (de ${dispensas.length} no total)` : ""}
        </div>
      )}
    </div>
  );
}